import {logger} from '@/utils/logger';
import {isAxiosError} from 'axios';
import {useEffect} from 'react';

/**
 * 무시해도 되는 에러인지 판별한다.
 * @param {*} error
 * @returns
 */
const canBeIgnored = (error) => {
  if (!error) {
    // 에러 객체가 없으므로 판단 불가
    return false;
  }

  return (
    isAxiosError(error) || // 외부 패키지의 Axios 에러 혹은 Axios 에러 중복 로깅 방지
    error.name === 'soop-navigation-error' || // Soop Navigation 내부에서 NavigationError로 처리되었는지 여부
    error.stack?.includes?.('/asset/app/liveplayer/player/dist/') // Player 영역에서 발생한 에러인지 여부
  );
};

/**
 * 웹 페이지의 전역 객체의 이벤트 핸들러를 사용, 처리되지 않은 에러를 인식해 에러 로그를 발송합니다.
 * (Sentry.globalHandlersIntegration의 자체적 구현)
 */
export const useGlobalErrorHandler = () => {
  useEffect(() => {
    /**
     * throw Error 처리
     * @param {ErrorEvent} event
     */
    const handleOnError = (event) => {
      if (
        process.env.NODE_ENV === 'development' ||
        process.env.NODE_ENV === 'test'
      ) {
        console.error(event.message);
        console.error(event);
      }

      if (canBeIgnored(event.error)) {
        return;
      }

      logger.error(event.error ?? new Error(event.message), {
        errorType: 'Unhandled Global Error',
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    /**
     * Promise reject 처리
     * @param {PromiseRejectionEvent} event
     */
    const handleOnUnhandledRejection = (event) => {
      if (
        process.env.NODE_ENV === 'development' ||
        process.env.NODE_ENV === 'test'
      ) {
        console.error(event.reason);
        console.error(event);
      }

      if (canBeIgnored(event.reason)) {
        return;
      }

      logger.error(
        event.reason instanceof Error ? event.reason : new Error(event.reason),
        {
          errorType: 'Unhandled Global Promise Rejection',
          reason: event.reason,
        },
      );
    };

    window.addEventListener('error', handleOnError);
    window.addEventListener('unhandledrejection', handleOnUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleOnError);
      window.removeEventListener(
        'unhandledrejection',
        handleOnUnhandledRejection,
      );
    };
  }, []);
};
